import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Home, LayoutGrid, List, BarChart, UserPlus, Users } from 'lucide-react';
import { CustomBreadcrumb, CustomButton, CustomSelect, CustomSearchableSelect } from '@/shared/components/atoms';
import { CustomCommonScreenHeader } from '@/shared/components/molecules';
import { useToastStore } from '@/shared/stores/toastStore';
import { useAuthStore } from '@/shared/stores/authStore';

const assignSchema = z.object({
    employee_id: z.string().min(1, 'Please select an employee to assign'),
});

type AssignFormData = z.infer<typeof assignSchema>;

export const AssignEmployeeToLevel = () => {
    const navigate = useNavigate();
    const { departmentId, typeId, levelId } = useParams<{ departmentId: string, typeId: string, levelId: string }>();
    const [isLoading, setIsLoading] = useState(false);
    const { showToast } = useToastStore();
    const { user } = useAuthStore();
    const schoolSlug = user?.school?.slug || '';

    // Mock unassigned employees
    const employees = [
        { label: 'Asif Dar', subLabel: 'asif@example.com', value: 'emp-1', image: 'https://ui-avatars.com/api/?name=Asif+Dar' },
        { label: 'Jane Smith', subLabel: 'jane@example.com', value: 'emp-2', image: 'https://ui-avatars.com/api/?name=Jane+Smith' },
        { label: 'John Doe', subLabel: 'john@example.com', value: 'emp-3', image: 'https://ui-avatars.com/api/?name=John+Doe' },
    ];

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
    } = useForm<AssignFormData>({
        resolver: zodResolver(assignSchema)
    });

    const onSubmit = async (data: AssignFormData) => {
        setIsLoading(true);
        try {
            // Mock API call
            await new Promise(resolve => setTimeout(resolve, 1000));
            showToast('Employee assigned successfully (Demo)', 'success');
            navigate(`/${schoolSlug}/school-admin/departments/${departmentId}/types/${typeId}/levels`);
        } catch (error) {
            showToast('Failed to assign employee', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{ padding: '1rem 1.5rem' }}>
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'Departments', path: `/${schoolSlug}/school-admin/departments`, icon: <LayoutGrid size={14} /> },
                    { label: 'Types', path: `/${schoolSlug}/school-admin/departments/${departmentId}/types`, icon: <List size={14} /> },
                    { label: 'Levels', path: `/${schoolSlug}/school-admin/departments/${departmentId}/types/${typeId}/levels`, icon: <BarChart size={14} /> },
                    { label: 'Assign Employee', icon: <UserPlus size={14} /> }
                ]}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <CustomCommonScreenHeader
                    title="Assign Employee"
                    subtitle="Easily map a staff member to this specific level"
                    icon={<UserPlus size={24} />}
                />
            </div>

            <div style={{
                maxWidth: '700px',
                margin: '2rem auto',
                background: 'var(--card-bg)',
                borderRadius: '16px',
                border: '1px solid var(--border-color)',
                padding: '2.5rem',
                boxShadow: 'var(--card-shadow)'
            }}>
                <div style={{ 
                    background: 'var(--bg-tertiary)', 
                    padding: '1rem', 
                    borderRadius: '8px',
                    marginBottom: '2rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                }}>
                    <BarChart size={24} color="var(--accent-primary)" />
                    <div>
                        <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Target Level ID</div>
                        <div style={{ fontWeight: 'bold' }}>{levelId}</div>
                    </div>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
                        <div>
                            <Controller
                                name="employee_id"
                                control={control}
                                render={({ field }) => (
                                    <CustomSearchableSelect
                                        label="Select Employee"
                                        options={employees}
                                        placeholder="Choose an employee..."
                                        leftIcon={<Users size={18} />}
                                        error={errors.employee_id?.message}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )}
                            />
                            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                                Only employees without a designated level are shown here.
                            </p>
                        </div>

                        <div style={{
                            marginTop: '1rem',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '1rem',
                            borderTop: '1px solid var(--border-color)',
                            paddingTop: '2rem'
                        }}>
                            <CustomButton
                                variant="outline"
                                color="secondary"
                                onClick={() => navigate(-1)}
                                disabled={isLoading}
                            >
                                Cancel
                            </CustomButton>
                            <CustomButton
                                type="submit"
                                variant="filled"
                                color="primary"
                                loading={isLoading}
                            >
                                Confirm Assignment
                            </CustomButton>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};
